import { useQuery } from '@tanstack/react-query';

import useGetPackageBySKU from 'components/billing/admin/plans/hooks/useGetPackageBySKU';
import { useHasBillingAccess } from 'components/billing/common/hooks/useHasBillingAccess';
import {
  useGetCurrentPlan,
  useGetCurrentProductPlansByType,
} from 'components/billing/context/useBilling';

import { api } from 'controllers/network/apiClient';

import type { ProductPlan } from 'components/billing/common/types';

/**
 * Extract the relevant fees from a ProductPlan
 */
const extractPlanFees = (plan?: ProductPlan) => {
  if (!plan) {
    return undefined;
  }

  const { id, productType, settings } = plan;
  const { recurringFees } = settings;

  const platformFee = recurringFees.find(
    (fee) => fee.type === 'AI_PLATFORM_FEE',
  )?.amountCents;

  // Ensure we only render the plan if all fees are defined
  if (
    platformFee === undefined ||
    settings.shopperSurchargeRate === undefined ||
    settings.azCampaignMessageRate === undefined ||
    settings.azVariantMessageRate === undefined ||
    settings.shopperAutomationRate === undefined
  ) {
    return undefined;
  }

  return {
    id,
    productType,
    platformFee,
    shopperResponseFee: settings.shopperSurchargeRate,
    itAutomationFee: settings.azVariantMessageRate,
    itCampaignFee: settings.azCampaignMessageRate,
    shopperAutomationFee: settings.shopperAutomationRate,
  };
};

const fetchBundledAIPlans = async () => {
  const response = await api.get<{ products: ProductPlan[] }>(
    '/v2/billing/plans/products/AI_BUNDLE',
  );

  return response.products;
};

const useBundledAIPlans = (enabled = true) =>
  useQuery({
    queryKey: ['billing', 'productPlans', 'AI_BUNDLE'],
    queryFn: fetchBundledAIPlans,
    enabled,
  });

export const useAvailableBundledAIPlan = (enabled = true) => {
  const { data: currentPlan } = useGetCurrentPlan();
  const { data: productPlans } = useBundledAIPlans(enabled);

  if (!productPlans || !currentPlan) {
    return undefined;
  }

  const productPlan = productPlans.find((productPlan) => {
    return currentPlan.package?.id === productPlan.package.id;
  });

  return extractPlanFees(productPlan);
};

export const useAIPlanBillingStatus = () => {
  const hasBillingAccess = useHasBillingAccess();

  const { data: currentPlans, isLoading } =
    useGetCurrentProductPlansByType('AI_BUNDLE');
  const currentPlan = currentPlans?.[0];
  const hasAIPlan = Boolean(currentPlan);

  const aiBundlePackage = useGetPackageBySKU('AI_BUNDLE');
  const isCustomPlan =
    currentPlan &&
    aiBundlePackage &&
    currentPlan.package.id === aiBundlePackage.id;

  // These two queries are the ones that actually back useAvailableBundledAIPlan
  // below. Tracked separately from `isLoading` above (which reflects hasAIPlan,
  // gated on a different, stricter enabled condition) so consumers of each can
  // use the one that matches what they're actually waiting on. Guarded against
  // the react-query v4 footgun where a disabled query reports isLoading: true
  // forever (see the same pattern at useBilling.ts:828).
  const {
    isLoading: isLoadingCurrentPlan,
    fetchStatus: currentPlanFetchStatus,
  } = useGetCurrentPlan();
  const {
    isLoading: isLoadingBundledAIPlans,
    fetchStatus: bundledAIPlansFetchStatus,
  } = useBundledAIPlans(hasBillingAccess);
  const defaultAIPlan = useAvailableBundledAIPlan(hasBillingAccess);

  const isLoadingDefaultAIPlan =
    (isLoadingCurrentPlan && currentPlanFetchStatus === 'fetching') ||
    (isLoadingBundledAIPlans && bundledAIPlansFetchStatus === 'fetching');

  return {
    /** The default AI Plan that matches the Shop's SMS Marketing Plan */
    defaultAIPlan,
    /** Whether the Shop has an AI Plan */
    hasAIPlan,
    /** The current AI Plan that the Shop is subscribed to */
    currentPlan: extractPlanFees(currentPlan),
    /** Whether the current AI Plan is a custom plan */
    isCustomPlan,
    /** Whether hasAIPlan/currentPlan is still loading */
    isLoading,
    /** Whether defaultAIPlan specifically is still loading */
    isLoadingDefaultAIPlan,
  };
};
